'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { logger } from '@/lib/logger.client';
import { useCartService } from '@/hooks/useCartService';
import { IOrder } from '@/services/types';
import { allocateBuyXGetYFreeUnits } from '@/themes/cray26/services/math';

import Img from './Image';

// Currency formatter utility
const toCurrencyFormat = (value: number, currency: string = 'ARS') => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency,
  }).format(Number(value));
};

type CartItem = IOrder;

type CartViewProps = {
  onCartUpdate?: (items: CartItem[]) => void;
};

export const CartView: React.FC<CartViewProps> = ({ onCartUpdate }) => {
  const cartService = useCartService();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    loadCartItems();
  }, []);

  const loadCartItems = () => {
    try {
      const items = cartService.getLocalCartItems();
      setCartItems(items);
      onCartUpdate?.(items);
      logger.debug({ count: items.length }, 'Loaded cart items');
    } catch (error) {
      logger.error({ error }, 'Failed to load cart items');
    }
  };

  const removeItem = (productId: string) => {
    try {
      const item = cartItems.find((i) => getCartItemId(i) === productId);
      if (item) {
        cartService.removeLocalCartItem(item as any);
        loadCartItems();
        logger.info({ productId }, 'Removed item from cart');
      }
    } catch (error) {
      logger.error({ productId, error }, 'Failed to remove cart item');
    }
  };

  const updateQuantity = (productId: string, newQuantity: number) => {
    try {
      const item = cartItems.find((i) => getCartItemId(i) === productId);
      if (item && newQuantity > 0) {
        const updated = { ...item, quantity: newQuantity };
        cartService.saveLocalCartItem(updated as any);
        loadCartItems();
        logger.debug({ productId, newQuantity }, 'Updated cart item quantity');
      }
    } catch (error) {
      logger.error({ productId, error }, 'Failed to update cart item quantity');
    }
  };

  const clearCart = () => {
    try {
      cartService.clearLocalCartItems();
      setCartItems([]);
      onCartUpdate?.([]);
      logger.info('Cart cleared');
    } catch (error) {
      logger.error({ error }, 'Failed to clear cart');
    }
  };

  const getCartItemId = (item: CartItem) => {
    const id = item.product?.id ?? item.id;
    const variantId = item.variant?.id;
    if (id == null) return '';
    return variantId ? `${String(id)}-${String(variantId)}` : String(id);
  };

  if (cartItems.length === 0) {
    return (
      <div data-testid="cart-empty" className="flex flex-col items-center justify-center py-16 px-4">
        <svg className="w-24 h-24 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">Tu carrito está vacío</h2>
        <p className="text-gray-500 mb-6 text-center">Añade productos para comenzar tu compra</p>
        <Link href="/search" className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
          Continuar Comprando
        </Link>
      </div>
    );
  }

  // ── Cart-level BUY_X_GET_Y free-unit allocation ──────────────────────────
  // Group eligible items by sale id, compute allocations, then build a lookup
  // map from cart-item key → allocated free units for that item.
  const buyXAllocations = (() => {
    const now = new Date();
    // Map<saleKey, { buyX, entries: { itemKey, unitPrice, qty }[] }>
    const groups = new Map<string, { buyX: number; entries: { itemKey: string; unitPrice: number; qty: number }[] }>();

    for (const item of cartItems) {
      const product = item.product as any;
      const sale = product?.sale ?? product?.effective_sale ?? product?.sales?.[0]?.sale ?? null;
      if (!sale || sale.discount_type !== 'BUY_X_GET_Y' || !(item.cart_discounts?.length)) continue;

      const validFrom = sale.valid_from ? new Date(sale.valid_from) : null;
      const validTo   = sale.valid_to   ? new Date(sale.valid_to)   : null;
      if ((validFrom && now < validFrom) || (validTo && now > validTo)) continue;

      const key = String(sale.id ?? sale.name ?? 'buyxgety');
      if (!groups.has(key)) {
        groups.set(key, { buyX: Number(sale.discount_value) || 1, entries: [] });
      }

      const priceFieldMap: Record<string, string> = {
        public: 'price_public_value',
        reseller: 'price_retail_value',
        wholesaler: 'price_wholesale_value',
      };
      const priceType = (item.price_type as string)?.toLowerCase() || 'public';
      const priceField = priceFieldMap[priceType] ?? 'price_public_value';
      const unitPrice = (product?.[priceField] as number) || 0;

      groups.get(key)!.entries.push({ itemKey: getCartItemId(item), unitPrice, qty: item.quantity || 0 });
    }

    // Allocate free units per group and flatten into a per-item map
    const result = new Map<string, number>();
    for (const { buyX, entries } of groups.values()) {
      const allocationInputs = entries.map((e) => ({ unitPrice: e.unitPrice, qty: e.qty }));
      const allocs = allocateBuyXGetYFreeUnits(allocationInputs, buyX);
      entries.forEach((entry, i) => {
        result.set(entry.itemKey, allocs[i] ?? 0);
      });
    }
    return result;
  })();

  return (
    <div data-testid="cart-root" className="w-full px-2">
      <div className="flex items-center justify-between mb-6">
        <h1 data-testid="cart-title" className="text-2xl font-bold text-gray-800">Carrito de Compras</h1>
        <button
          onClick={clearCart}
          data-testid="cart-clear-button"
          className="cursor-pointer flex items-center gap-2 px-2 py-2 text-xs font-medium text-gray-500 border border-gray-300 rounded-md hover:bg-gray-300/10 transition-colors"
          aria-label="Vaciar carrito"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Vaciar carrito
        </button>
      </div>
      <div data-testid="cart-items-list" className="space-y-4">
        {cartItems.map((item) => (
          <CartItemCard
            key={getCartItemId(item)}
            item={item}
            allocatedFreeQty={buyXAllocations.get(getCartItemId(item)) ?? 0}
            onRemove={() => removeItem(getCartItemId(item))}
            onUpdateQuantity={(qty) => updateQuantity(getCartItemId(item), qty)}
          />
        ))}
      </div>
    </div>
  );
};

interface CartItemCardProps {
  item: CartItem;
  /** Cart-level allocated free units for this item (BUY_X_GET_Y). */
  allocatedFreeQty?: number;
  onRemove: () => void;
  onUpdateQuantity: (qty: number) => void;
}

function CartItemCard({ item, allocatedFreeQty = 0, onRemove, onUpdateQuantity }: CartItemCardProps) {
  const priceType = (item.price_type as 'public' | 'reseller' | 'wholesaler') || 'public';
  // Map price type to flat field name
  const priceFieldMap = {
    public: 'price_public_value',
    reseller: 'price_retail_value',
    wholesaler: 'price_wholesale_value',
  };
  const priceField = priceFieldMap[priceType] as keyof typeof item.product;
  const originalPrice = (item.product?.[priceField] as number) || 0;

  // Coerce quantity to a number; the order model may have it as null/undefined.
  const quantity = typeof item.quantity === 'number' ? item.quantity : 1;

  const product = item.product as any;

  // Calculate discounted price: prefer server-provided net price, then sale-based calculation.
  let price = originalPrice;
  let hasSale = false;
  // BUY_X_GET_Y: quantity-based promo — no per-unit price reduction.
  // Use cart-level allocation passed in from CartView (not per-item formula).
  let isBuyXGetY = false;

  if (product.discounted_price !== undefined && product.discounted_price < originalPrice) {
    // Server already computed the discounted price.
    price = product.discounted_price;
    hasSale = true;
  } else {
    // Fall back to sale-based calculation.
    // Resolve the sale from whichever field the API populated.
    const hasDiscount = item.cart_discounts && item.cart_discounts.length > 0;
    const sale =
      hasDiscount
        ? (product?.sale ?? product?.effective_sale ?? product?.sales?.[0]?.sale ?? null)
        : null;

    if (sale && sale.discount_value > 0) {
      // Validate the sale date range before showing the discount.
      const now = new Date();
      const validFrom = sale.valid_from ? new Date(sale.valid_from) : null;
      const validTo = sale.valid_to ? new Date(sale.valid_to) : null;
      const isActive = (!validFrom || now >= validFrom) && (!validTo || now <= validTo);

      if (isActive) {
        hasSale = true;
        if (sale.discount_type === 'PERCENTAGE') {
          price = Math.max(0, originalPrice * (1 - sale.discount_value / 100));
        } else if (sale.discount_type === 'FIXED') {
          price = Math.max(0, originalPrice - sale.discount_value);
        } else if (sale.discount_type === 'BUY_X_GET_Y') {
          // No per-unit price reduction — discount is on free units (cart-level).
          isBuyXGetY = true;
          hasSale = allocatedFreeQty > 0; // show sale badge only when promo is actually active
        }
      }
    }
  }

  // For BUY_X_GET_Y: subtotal = full price × qty minus cart-level free unit value
  const subtotal = isBuyXGetY
    ? originalPrice * quantity - allocatedFreeQty * originalPrice
    : price * quantity;
  const imageUrl = (item.product as any)?.picture?.url || '/placeholder.webp';

  useEffect(() => {
    logger.debug({ priceField, originalPrice }, 'Rendering cart prices');
  }, [item]);

  return (
    <div data-testid="cart-item" className="flex gap-4 p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
      {/* Product Image */}
      <div className="shrink-0">
        <Img src={imageUrl} alt={item.product?.name || 'Product'} className="w-24 h-24 object-cover rounded-md" />
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{item.product?.name}</h3>

        {/* Variant */}
        {(() => {
          const variant = item.variant as any;
          if (!variant) return null;
          return (
            <div className="flex flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                {variant.color && (
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 rounded-full border border-gray-300" style={{ backgroundColor: variant.color.hex }} />
                    {variant.color.name}
                  </span>
                )}
                {variant.size && <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{variant.size.name}</span>}
              </div>
            </div>
          );
        })()}

        {/* Price and Quantity */}
        <div className="flex flex-wrap items-center gap-4 mt-3">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              {hasSale && !isBuyXGetY && (
                <span className="text-sm text-gray-400" style={{ textDecoration: 'line-through' }}>
                  {toCurrencyFormat(originalPrice)}
                </span>
              )}
              <span className={`text-base ${hasSale ? 'text-primary font-semibold' : 'text-gray-700'}`}>
                {toCurrencyFormat(isBuyXGetY ? originalPrice : price)}
              </span>
            </div>
            {isBuyXGetY && allocatedFreeQty > 0 && (
              <span className="text-xs text-green-600 font-medium">
                {allocatedFreeQty === 1 ? '1 unidad gratis' : `${allocatedFreeQty} unidades gratis`}
              </span>
            )}
          </div>
          <span className="text-gray-400">×</span>
          <div className="flex items-center border border-gray-300 rounded-lg">
            <button onClick={() => onUpdateQuantity(Math.max(1, quantity - 1))} data-testid="qty-decrement" className="px-3 py-1 hover:bg-gray-100 transition-colors" aria-label="Disminuir cantidad">
              −
            </button>
            <input
              type="number"
              min="1"
              max="99"
              value={quantity}
              onChange={(e) => {
                const newQty = parseInt(e.target.value) || 1;
                onUpdateQuantity(Math.max(1, Math.min(99, newQty)));
              }}
              className="w-16 text-center border-x border-gray-300 py-1 focus:outline-none"
              data-testid="qty-input"
            />
            <button onClick={() => onUpdateQuantity(Math.min(99, quantity + 1))} data-testid="qty-increment" className="px-3 py-1 hover:bg-gray-100 transition-colors" aria-label="Aumentar cantidad">
              +
            </button>
          </div>
          <span className="text-base flex-1 font-semibold text-gray-800">= {toCurrencyFormat(subtotal)}</span>
        </div>
      </div>

      {/* Remove Button */}
      <button
        onClick={onRemove}
        data-testid="cart-item-remove"
        className="shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-error hover:bg-error/10 rounded-full transition-colors"
        aria-label="Eliminar producto"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default CartView;
